import type { ReactNode } from 'react';

import { useMatches } from 'react-router-dom';

import { DataGridSkeleton } from '@/components/ui/data-grid-skeleton';
import { PageSkeleton } from '@/components/ui/page-skeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ConnectorsTableSkeleton } from '@/components/tenants/connectors/connectors-table-skeleton';
import { DashboardSkeleton } from '@/components/tenants/home/dashboard-skeleton';

/**
 * Keys a route's `handle.skeleton` to a layout-shaped loading placeholder, so
 * the route-level Suspense fallback can paint the *correct* shape the instant a
 * nav link is clicked — instead of flashing the generic {@link PageSkeleton}
 * and then swapping to the page's own skeleton once its chunk downloads.
 *
 * These presets mirror the page chrome (header + optional tab bar) plus the
 * page's data-loading body skeleton, so the chunk-download → data-load → content
 * hand-off reads as one continuous shimmer.
 */
/**
 * Identifies which layout-shaped loading placeholder a route renders. Keys are
 * named by *shape*, not page, so they can be reused across pages with the same
 * layout:
 * - `dashboard`     — the home dashboard (hero + chart + connector grid)
 * - `table`         — header + data-grid (Roles, Teams, Clients, Evaluators…)
 * - `tabbed-table`  — header + tab bar + data-grid (Secrets, Members)
 * - `tabbed-cards`  — header + tab bar + connector card grid (Connectors, Providers)
 */
export type RouteSkeletonKey =
  | 'dashboard'
  | 'table'
  | 'tabbed-table'
  | 'tabbed-cards';

/** Shared page padding — mirrors the scoped layout's content wrapper. */
function PageShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="mr-auto w-full max-w-screen-2xl px-5 pt-3 sm:px-4"
      aria-hidden="true"
    >
      {children}
    </div>
  );
}

/** Title + description bars matching EditablePageHeader's read-only header. */
function HeaderSkeleton() {
  return (
    <div className="flex flex-col gap-2 sm:gap-1">
      <Skeleton className="h-7 w-48" />
      <Skeleton className="h-4 w-72 max-w-full" />
    </div>
  );
}

/** A row of tab-label bars with the same bottom border as the live tab bar. */
function TabBarSkeleton({ count }: { count: number }) {
  return (
    <div className={cn('border-border/50 mt-4 flex gap-4 border-b-2 pb-2')}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-5 w-20" />
      ))}
    </div>
  );
}

function TablePageSkeleton() {
  return (
    <PageShell>
      <HeaderSkeleton />
      <div className="mt-6">
        <DataGridSkeleton />
      </div>
    </PageShell>
  );
}

function TabbedCardsPageSkeleton() {
  return (
    <PageShell>
      <HeaderSkeleton />
      <TabBarSkeleton count={2} />
      <div className="pt-3">
        <ConnectorsTableSkeleton />
      </div>
    </PageShell>
  );
}

function TabbedTablePageSkeleton() {
  return (
    <PageShell>
      <HeaderSkeleton />
      <TabBarSkeleton count={2} />
      <div className="pt-3">
        <DataGridSkeleton />
      </div>
    </PageShell>
  );
}

const routeSkeletons: Record<RouteSkeletonKey, ReactNode> = {
  dashboard: <DashboardSkeleton />,
  table: <TablePageSkeleton />,
  'tabbed-table': <TabbedTablePageSkeleton />,
  'tabbed-cards': <TabbedCardsPageSkeleton />,
};

/**
 * Route-aware Suspense fallback. Reads the deepest matched route's
 * `handle.skeleton` key and renders the matching layout skeleton, falling back
 * to the neutral {@link PageSkeleton} for routes that don't declare one.
 */
export function RouteSkeleton() {
  const matches = useMatches();
  const key = [...matches]
    .reverse()
    .map(
      (m) => (m.handle as { skeleton?: RouteSkeletonKey } | undefined)?.skeleton
    )
    .find((k): k is RouteSkeletonKey => k != null);

  return <>{(key && routeSkeletons[key]) ?? <PageSkeleton />}</>;
}
